/* Two-way project bridges.
   - Contract Generator (contracts.studiogy.design): hand a project over as a
     ProjectBrief to draft a contract; receive projects back on /import.
   - Project Studio (studio.studiogy.design): create the job in the Bid Manager
     or the Project Manager, carrying the drawing register across, and link back.
   URL-param handoff (base64 JSON) so it works in embedded + real browsers. */

import type { Project, StudioLinkRef } from "./types";
import { STUDIO } from "./store";

const CONTRACT_GEN_URLS = {
  prod: "https://contracts.studiogy.design",
  alt: "https://contract-gen.renauto.workers.dev",
  dev: "http://localhost:5178",
};
export function contractGenUrl(): string {
  if (typeof location !== "undefined" && location.hostname === "localhost") return CONTRACT_GEN_URLS.dev;
  return CONTRACT_GEN_URLS.prod;
}

const STUDIO_URLS = { prod: "https://studio.studiogy.design", dev: "http://localhost:4347" };
export function studioUrl(): string {
  if (typeof location !== "undefined" && location.hostname === "localhost") return STUDIO_URLS.dev;
  return STUDIO_URLS.prod;
}
export function studioOrigin(): string { try { return new URL(studioUrl()).origin; } catch { return STUDIO_URLS.prod; } }

/* ---- utf-8 safe base64 ---- */
export function b64encode(obj: unknown): string {
  return btoa(unescape(encodeURIComponent(JSON.stringify(obj))));
}
export function b64decode<T = any>(s: string): T | null {
  try { return JSON.parse(decodeURIComponent(escape(atob(s)))) as T; } catch { return null; }
}

/* ---- gzip + base64url ----
   A drawing register raw-encodes to ~16KB of URL, which browsers refuse to
   navigate to and proxies cap at 8–16KB. Gzip takes the same payload to ~2KB
   and keeps a 200-sheet set well inside every limit. base64url avoids the
   percent-escaping that would undo the saving. */
export async function gzipB64url(obj: unknown): Promise<string | null> {
  try {
    if (typeof CompressionStream === "undefined") return null;
    const bytes = new TextEncoder().encode(JSON.stringify(obj));
    const stream = new Blob([bytes]).stream().pipeThrough(new CompressionStream("gzip"));
    const out = new Uint8Array(await new Response(stream).arrayBuffer());
    let bin = "";
    for (let i = 0; i < out.length; i++) bin += String.fromCharCode(out[i]);
    return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  } catch { return null; }
}

/* ---- Atelier project → Contract Generator ProjectBrief ---- */
export function projectToBrief(p: Project): Record<string, unknown> {
  const scope = p.brief.sections.map((s) => s.body).filter(Boolean).join(" ");
  return {
    standard: "AIA_B101_2017", // Owner–Architect design-services agreement
    projectName: p.name,
    projectLocation: p.location,
    contractReference: p.code,
    atelierProjectId: p.id,
    worksDescription: `${p.type}${p.area ? " — " + p.area : ""}. ${scope}`.trim(),
    employer: { name: p.client.company || p.client.name, address: "", representative: p.client.name, email: p.client.email },
    contractor: { name: "", address: "" },
    engineer: { name: STUDIO.name, address: "", representative: STUDIO.principal, email: STUDIO.email },
    contractPrice: { amount: p.budget, currency: p.currency },
    estimatedConstructionCost: { amount: p.budget, currency: p.currency },
    designFeeBasis: "percentage",
    timeForCompletion: "",
    defectsNotification: "12 months",
    governingLaw: "",
    rulingLanguage: "English",
    disputeResolution: "",
    narrative: p.brief.sections.filter((s) => s.body).map((s) => ({ heading: s.title, text: s.body })),
    specialProvisions: [],
    drawings: [],
    preparedBy: { org: STUDIO.name, name: STUDIO.principal, role: "Architect" },
  };
}

/** Open the Contract Generator with this project as a draft contract. */
export function sendToContractGen(p: Project) {
  const url = `${contractGenUrl()}/?atelier=${encodeURIComponent(b64encode(projectToBrief(p)))}`;
  window.open(url, "_blank", "noopener");
}

/* ---- Atelier → Project Studio (Bid Manager / Project Manager) ---- */
export type StudioTarget = "pm" | "bid";

/** One sheet of the drawing register, trimmed for the handoff. Per-sheet
 *  `contents` are deliberately left behind — the studio tracks revisions and
 *  files, and dropping them keeps the URL payload small. */
export interface RegisterSheet {
  code: string; title: string; discipline: string;
  kind?: string; scale?: string; sheet?: string; revision?: string; status?: string; phase?: string;
}

/** An issued drawing or a render, pointed at the image the studio can pull.
 *  URLs are absolute — a bare "/api/file/x" would resolve against the Project
 *  Studio's own origin and 404. */
export interface HandoffImage {
  kind: "sheet" | "render";
  code?: string; title: string; discipline?: string; revision?: string;
  url: string; note?: string; on?: string;
}

export interface StudioHandoff {
  v: 1;
  source: "atelier";
  target: StudioTarget;
  atelierProjectId: string;
  /** The job already created over there, when this is a re-sync rather than a
   *  first hand-off — lets the studio update in place instead of duplicating. */
  existingId?: string;
  name: string; code: string; type: string; location: string; area: string;
  budget: number; currency: string; description: string;
  client: { name: string; company: string; email: string };
  register: RegisterSheet[];
  /** Drawings actually issued and renders presented — the pictures, not just
   *  the register rows. Fetched by the studio at import. */
  images?: HandoffImage[];
  backUrl: string;
}

/** Make a stored image reference reachable from another origin. */
function absolute(url: string | undefined, origin: string): string | null {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("data:")) return null;     // inline bytes never survive a URL payload
  return url.startsWith("/") ? origin + url : null;
}

/** Every picture worth mirroring onto the site project, newest revision first. */
export function projectImages(p: Project, origin: string): HandoffImage[] {
  const out: HandoffImage[] = [];
  for (const r of p.reviews ?? []) {
    for (const s of r.sheets) {
      const url = absolute(s.url, origin);
      if (url) out.push({ kind: "sheet", code: s.code, title: s.title, discipline: s.discipline, revision: s.revision, url, note: s.summary, on: s.revisedOn ?? r.createdOn });
    }
  }
  for (const c of p.concepts ?? []) {
    const url = absolute(c.url, origin);
    if (url) out.push({ kind: "render", title: c.title, url, note: c.note, on: c.createdOn });
  }
  for (const s of p.presentation?.spaces ?? []) {
    const url = absolute((s as { url?: string }).url, origin);
    if (url) out.push({ kind: "render", title: `${s.name} — ${s.option}`, url, note: s.description });
  }
  return out;
}

export function projectToStudioHandoff(p: Project, target: StudioTarget): StudioHandoff {
  const scope = p.brief.sections.map((s) => s.body).filter(Boolean).join(" ");
  const origin = typeof location !== "undefined" ? location.origin : `https://${STUDIO.domain}`;
  /* The works value the site/tender runs against is the construction budget —
     which on a contracted project lives on the billing structure (in its own
     currency), not the design-side p.budget that may sit at zero. */
  const constructionBudget = p.billing?.constructionBudget || p.budget;
  const budgetCurrency = p.billing?.constructionBudget ? p.billing.currency : p.currency;
  return {
    v: 1, source: "atelier", target,
    atelierProjectId: p.id,
    existingId: p.links?.[target]?.id,
    name: p.name, code: p.code, type: p.type, location: p.location, area: p.area,
    budget: constructionBudget, currency: budgetCurrency,
    description: `${p.type}${p.area ? " — " + p.area : ""}. ${scope}`.trim(),
    client: { name: p.client.name, company: p.client.company || "", email: p.client.email || "" },
    register: (p.deliverables ?? []).map((d) => ({
      code: d.code, title: d.title, discipline: d.discipline,
      kind: d.kind, scale: d.scale, sheet: d.sheet, revision: d.revision, status: d.status, phase: d.phase,
    })),
    images: projectImages(p, origin),
    backUrl: `${origin}/studio/${p.id}/program`,
  };
}

/** Open the Project Studio's import screen with this project + its register.
 *  Opened WITH an opener so the studio can post the created job back and we can
 *  store the link (origin-checked on both ends).
 *
 *  The tab is opened synchronously — compressing first would spend the user
 *  gesture and get the popup blocked — then pointed at the payload once ready. */
export function sendToStudio(p: Project, target: StudioTarget) {
  const w = window.open("", "_blank");
  const payload = projectToStudioHandoff(p, target);
  void gzipB64url(payload).then((gz) => {
    const url = gz
      ? `${studioUrl()}/import?d=${gz}`
      : `${studioUrl()}/import?data=${encodeURIComponent(b64encode(payload))}`;   // no CompressionStream — send it raw
    if (w && !w.closed) w.location.replace(url);
    else window.location.href = url;                                              // popup blocked — go in this tab
  });
}

/** Listen for "project created" from the Project Studio tab we opened. */
export function listenForStudioLink(onLink: (atelierProjectId: string, target: StudioTarget, ref: StudioLinkRef) => void): () => void {
  const handler = (e: MessageEvent) => {
    if (e.origin !== studioOrigin()) return;                       // only our studio may link back
    const d: any = e.data;
    if (!d || d.type !== "project-studio:created" || !d.atelierProjectId || !d.id) return;
    const target: StudioTarget = d.target === "bid" ? "bid" : "pm";
    onLink(String(d.atelierProjectId), target, {
      id: String(d.id), code: String(d.code || ""), url: String(d.url || ""), sheets: Number(d.sheets) || 0, at: new Date().toISOString(),
    });
  };
  window.addEventListener("message", handler);
  return () => window.removeEventListener("message", handler);
}

/* ---- Contract Generator → Atelier inbound project (/import?data=) ---- */
export interface InboundProject {
  name: string; type: string; location: string; area: string; budget: number; currency: string;
  clientName: string; clientCompany: string; clientEmail: string;
  briefSections?: { title: string; body: string }[];
}
export function readInboundProject(search: string): InboundProject | null {
  const raw = new URLSearchParams(search).get("data");
  if (!raw) return null;
  const o = b64decode<any>(raw);
  if (!o || !o.name) return null;
  return {
    name: String(o.name), type: o.type || "Private Residence", location: o.location || "",
    area: o.area || "", budget: Number(o.budget) || 0, currency: o.currency || "USD",
    clientName: o.clientName || "", clientCompany: o.clientCompany || "", clientEmail: o.clientEmail || "",
    briefSections: Array.isArray(o.briefSections) ? o.briefSections : undefined,
  };
}
